import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { formatTimestampSafe } from '@/lib/utils/formatTimestamp';

const POLICY_ENDPOINT = 'site-policy';

const roleOptions = [
  { value: 'administrator', label: 'Administrators' },
  { value: 'editor', label: 'Editors' },
  { value: 'author', label: 'Authors' },
  { value: 'shop_manager', label: 'Shop Managers' },
];

const formatTimestamp = (value?: string | null) => (value ? formatTimestampSafe(value) : '—');

export default function DomainSettings() {
  const { domain, managedSite } = useOutletContext<{
    domain: { id: string; domain_name: string; vuln_enrichment_enabled?: boolean | null };
    managedSite?: { id?: string | null } | null;
  }>();

  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoMode, setAutoMode] = useState<'allowlist' | 'all'>('allowlist');
  const [autoAllowlist, setAutoAllowlist] = useState<string[]>([]);
  const [coreAutoEnabled, setCoreAutoEnabled] = useState(false);
  const [autoInput, setAutoInput] = useState('');

  const [passwordMinLength, setPasswordMinLength] = useState(12);
  const [passwordEnforceMin, setPasswordEnforceMin] = useState(true);
  const [passwordEnforceBreached, setPasswordEnforceBreached] = useState(false);
  const [passwordRoles, setPasswordRoles] = useState<string[]>(['administrator']);

  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [vulnEnrichmentEnabled, setVulnEnrichmentEnabled] = useState<boolean>(true);
  const [vulnEnrichmentSaving, setVulnEnrichmentSaving] = useState(false);
  const [loginRequired, setLoginRequired] = useState(false);
  const vulnUiEnabled = import.meta.env.VITE_VULN_ENRICHMENT_ENABLED === 'true';

  const policyQuery = useQuery({
    queryKey: ['site-policy', managedSite?.id],
    queryFn: async () => {
      if (!managedSite?.id) return null;
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) {
        setLoginRequired(true);
        throw new Error('Not authenticated');
      }
      setLoginRequired(false);
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');
      const response = await fetch(
        `${supabaseUrl}/functions/v1/${POLICY_ENDPOINT}?site_id=${managedSite.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-SS-Auth': 'user',
          },
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (response.status === 403) {
        console.warn('[site-policy] forbidden response', payload);
      }
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to load policy');
      }
      return payload;
    },
    enabled: !!managedSite?.id,
  });

  const inventoryQuery = useQuery({
    queryKey: ['domain-plugin-inventory', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_component_inventory')
        .select('components_json, captured_at')
        .eq('domain_id', domain.id)
        .order('captured_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data ?? null;
    },
    enabled: Boolean(domain?.id),
  });

  const inventoryPluginSlugs = useMemo(() => {
    const components = inventoryQuery.data?.components_json as any;
    const plugins = Array.isArray(components?.plugins) ? components.plugins : [];
    const slugs = plugins
      .map((plugin: any) => (typeof plugin?.slug === 'string' ? plugin.slug.trim().toLowerCase() : ''))
      .filter((slug: string) => slug.length > 0);
    return Array.from(new Set(slugs)).sort((a: string, b: string) => a.localeCompare(b)) as string[];
  }, [inventoryQuery.data]);

  const policy = policyQuery.data?.policy ?? {};
  const policyTier = typeof policy.tier === 'string' ? policy.tier : 'monitor';
  const policyLocked = Boolean(policy.locked);
  const isManagedLocked = policyTier === 'managed' && policyLocked;
  const securityControlsWriteEnabled = policyQuery.data?.rollout?.securityControlsWriteEnabled === true;
  const rolloutReadOnly = !securityControlsWriteEnabled;
  const controlsReadOnly = isManagedLocked || rolloutReadOnly;

  useEffect(() => {
    if (!policyQuery.data) return;
    const securityControls = policy.security_controls ?? {};
    const autoupdate = securityControls.autoupdate ?? {};
    const password = securityControls.password_policy ?? {};
    setAutoEnabled(Boolean(autoupdate.enabled));
    setAutoMode(autoupdate.mode === 'all' ? 'all' : 'allowlist');
    setAutoAllowlist(Array.isArray(autoupdate.allowlist) ? autoupdate.allowlist.map(String) : []);
    setCoreAutoEnabled(Boolean(autoupdate.core_enabled));

    const minLength = Number(password.min_length ?? 12);
    setPasswordMinLength(Number.isFinite(minLength) ? minLength : 12);
    setPasswordEnforceMin(password.enforce_min_length !== false);
    setPasswordEnforceBreached(Boolean(password.enforce_breached));
    setPasswordRoles(
      Array.isArray(password.roles) && password.roles.length > 0
        ? password.roles.map(String)
        : ['administrator']
    );
  }, [policyQuery.data, policy]);

  useEffect(() => {
    const enabled = domain?.vuln_enrichment_enabled;
    setVulnEnrichmentEnabled(enabled !== false);
  }, [domain?.vuln_enrichment_enabled]);

  const addAllowlistItem = () => {
    const value = autoInput.trim();
    if (!value) return;
    const normalized = value.toLowerCase();
    if (!/^[a-z0-9][a-z0-9-_]*$/.test(normalized)) {
      toast.error('Plugin slug must use letters, numbers, hyphens, or underscores.');
      return;
    }
    setAutoAllowlist((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
    setAutoInput('');
  };

  const removeAllowlistItem = (item: string) => {
    setAutoAllowlist((prev) => prev.filter((entry) => entry !== item));
  };

  const handleSyncNow = async () => {
    if (!managedSite?.id) {
      toast.error('Connect the domain before syncing.');
      return;
    }
    setSyncing(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) {
        setLoginRequired(true);
        throw new Error('Not authenticated');
      }
      setLoginRequired(false);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');

      // Queue a refresh_status command to trigger immediate policy refetch
      const response = await fetch(`${supabaseUrl}/functions/v1/trigger-policy-sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-SS-Auth': 'user',
        },
        body: JSON.stringify({
          site_id: managedSite.id,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (response.status === 403) {
        console.warn('[trigger-policy-sync] forbidden response', payload);
      }
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to trigger sync');
      }

      toast.success('Sync triggered. Plugin will update within 5 minutes.');
      await policyQuery.refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to trigger sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async () => {
    if (!managedSite?.id) {
      toast.error('Connect the domain before editing settings.');
      return;
    }
    setSaving(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) {
        setLoginRequired(true);
        throw new Error('Not authenticated');
      }
      setLoginRequired(false);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');

      const payload = {
        tier: policyTier,
        owasp_enforcement: policy.owasp_enforcement ?? {},
        controls: policy.controls ?? [],
        security_controls: {
          autoupdate: {
            enabled: autoEnabled,
            mode: autoMode,
            allowlist: autoAllowlist,
            core_enabled: coreAutoEnabled,
          },
          password_policy: {
            min_length: passwordMinLength,
            enforce_min_length: passwordEnforceMin,
            enforce_breached: passwordEnforceBreached,
            roles: passwordRoles.length > 0 ? passwordRoles : ['administrator'],
          },
        },
      };

      const response = await fetch(
        `${supabaseUrl}/functions/v1/${POLICY_ENDPOINT}?site_id=${managedSite.id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-SS-Auth': 'user',
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (response.status === 403) {
        console.warn('[site-policy] forbidden response', result);
      }
      if (!response.ok || result?.error) {
        throw new Error(result?.error || 'Unable to update security controls');
      }
      toast.success('Security controls updated.');
      await policyQuery.refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Unable to update security controls');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVulnEnrichment = async (enabled: boolean) => {
    if (!managedSite?.id) {
      toast.error('Connect the domain before editing vulnerability enrichment.');
      return;
    }
    setVulnEnrichmentSaving(true);
    try {
      const { error } = await supabase
        .from('domains')
        .update({ vuln_enrichment_enabled: enabled })
        .eq('id', domain.id);
      if (error) throw error;
      setVulnEnrichmentEnabled(enabled);
      toast.success(enabled ? 'Vulnerability intelligence enabled.' : 'Vulnerability intelligence disabled.');
    } catch (err: any) {
      toast.error(err?.message || 'Unable to update vulnerability intelligence');
    } finally {
      setVulnEnrichmentSaving(false);
    }
  };

  const vulnStatusQuery = useQuery({
    queryKey: ['vuln-intel-status', domain.id],
    queryFn: async () => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');
      const response = await fetch(
        `${supabaseUrl}/functions/v1/vuln-intel-status?domain_id=${domain.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to load vulnerability status');
      }
      return payload;
    },
    enabled: Boolean(domain?.id) && vulnUiEnabled,
  });

  const wordfenceSync = vulnStatusQuery.data?.wordfence?.selected?.fetched_at ?? null;
  const wpvulnSync = vulnStatusQuery.data?.wpvulnerability?.last_fetch_at ?? null;
  const dataSourceEnabled = vulnStatusQuery.data?.enabled !== false;

  if (!managedSite?.id) {
    return (
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Security Controls</CardTitle>
          <CardDescription>Connect this domain to configure policy-driven controls.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            This domain is not connected to the ShadowScan connector yet. Connect the site to edit
            auto-updates and password policy (change/reset).
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loginRequired) {
    return (
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Security Controls</CardTitle>
          <CardDescription>Sign in to view and manage security controls.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Your session has expired. Please sign in again to continue.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Security Controls</CardTitle>
          <CardDescription>
            Manage policy-driven controls for {domain.domain_name}. Detection runs continuously; enforcement
            stays conservative by default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <Badge variant="outline">Policy tier: {policyTier}</Badge>
              <span>Last updated: {formatTimestamp(policyQuery.data?.updated_at)}</span>
              {isManagedLocked && <Badge variant="secondary">Managed by ShadowScan</Badge>}
            </div>
            {managedSite?.id && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncNow}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            )}
          </div>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              Settings are synced to your WordPress site automatically. Changes typically appear within
              10 minutes, or click "Sync Now" to trigger an immediate update (applies within 5 minutes).
            </p>
            {(isManagedLocked || rolloutReadOnly) && (
              <p className="text-warning">
                {isManagedLocked
                  ? 'Controls are locked by ShadowScan policy. Contact support to request changes.'
                  : 'Policy controls are currently in read-only rollout mode while enforcement parity is validated.'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Auto-updates</CardTitle>
          <CardDescription>Enable plugin auto-updates while keeping themes excluded.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Enable plugin auto-updates (themes excluded)</Label>
              <p className="text-xs text-muted-foreground">Prefer allowlist for safety-sensitive sites.</p>
            </div>
            <Switch
              checked={autoEnabled}
              onCheckedChange={setAutoEnabled}
              disabled={controlsReadOnly}
              aria-label="Enable plugin auto-updates"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-sm font-medium">Update mode</Label>
            <Select value={autoMode} onValueChange={(value) => setAutoMode(value as 'all' | 'allowlist')} disabled={controlsReadOnly}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allowlist">Allowlist (recommended)</SelectItem>
                <SelectItem value="all">All plugins</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {autoMode === 'allowlist' && (
            <div className="space-y-3">
              {inventoryPluginSlugs.length > 0 ? (
                <>
                  <div className="rounded-md border border-border/60 p-3 space-y-2">
                    {inventoryPluginSlugs.map((slug) => (
                      <label key={slug} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Checkbox
                          checked={autoAllowlist.includes(slug)}
                          onCheckedChange={(checked) => {
                            setAutoAllowlist((prev) =>
                              checked ? (prev.includes(slug) ? prev : [...prev, slug]) : prev.filter((entry) => entry !== slug)
                            );
                          }}
                          disabled={controlsReadOnly}
                        />
                        {slug}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Select plugins from your latest site inventory. Last inventory: {formatTimestamp(inventoryQuery.data?.captured_at ?? null)}.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      value={autoInput}
                      onChange={(event) => setAutoInput(event.target.value)}
                      placeholder="plugin-slug"
                      disabled={controlsReadOnly}
                    />
                    <Button type="button" variant="outline" onClick={addAllowlistItem} disabled={controlsReadOnly}>
                      Add plugin
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {autoAllowlist.length === 0 && (
                      <span className="text-xs text-muted-foreground">No plugins allowlisted yet.</span>
                    )}
                    {autoAllowlist.map((item) => (
                      <Badge key={item} variant="secondary" className="flex items-center gap-2">
                        <span>{item}</span>
                        {!controlsReadOnly && (
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => removeAllowlistItem(item)}
                          >
                            Remove
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Inventory is not available yet. Enter plugin slugs manually (for example: akismet).
                  </p>
                </>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Enable WordPress core auto-updates (recommended for security)</Label>
              <p className="text-xs text-muted-foreground">
                If your host already manages core updates, this can stay off.
              </p>
            </div>
            <Switch
              checked={coreAutoEnabled}
              onCheckedChange={setCoreAutoEnabled}
              disabled={controlsReadOnly}
              aria-label="Enable WordPress core auto-updates"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Password policy (change/reset)</CardTitle>
          <CardDescription>Apply minimum length and breached-password checks for admin roles.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-2">
            <Label className="text-sm font-medium">Minimum length</Label>
            <Input
              type="number"
              min={8}
              value={passwordMinLength}
              onChange={(event) => setPasswordMinLength(Number(event.target.value))}
              disabled={controlsReadOnly}
              className="w-full sm:w-40"
            />
            <p className="text-xs text-muted-foreground">Use 12+ characters for administrator accounts.</p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Enforce minimum length</Label>
              <p className="text-xs text-muted-foreground">Admins must meet the minimum length to save passwords.</p>
            </div>
            <Switch
              checked={passwordEnforceMin}
              onCheckedChange={setPasswordEnforceMin}
              disabled={controlsReadOnly}
              aria-label="Enforce minimum length"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Block breached passwords</Label>
              <p className="text-xs text-muted-foreground">Uses the HIBP k-anonymity API.</p>
            </div>
            <Switch
              checked={passwordEnforceBreached}
              onCheckedChange={setPasswordEnforceBreached}
              disabled={controlsReadOnly}
              aria-label="Block breached passwords"
            />
          </div>

          <div className="grid gap-3">
            <Label className="text-sm font-medium">Roles in scope</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {roleOptions.map((role) => (
                <label key={role.value} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={passwordRoles.includes(role.value)}
                    onCheckedChange={(checked) => {
                      setPasswordRoles((prev) =>
                        checked
                          ? [...prev, role.value]
                          : prev.filter((entry) => entry !== role.value)
                      );
                    }}
                    disabled={controlsReadOnly}
                  />
                  {role.label}
                </label>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Admin access location rules</CardTitle>
          <CardDescription>Location rules are managed in the Admin Access Location Rules page.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline">
              Mode: {policy.security_controls?.admin_geo_guard?.mode ?? 'observe'}
            </Badge>
            <Badge variant="outline">
              Allowlist: {Array.isArray(policy.security_controls?.admin_geo_guard?.allow_countries)
                ? policy.security_controls?.admin_geo_guard?.allow_countries.length
                : 0}
            </Badge>
            <Badge variant="outline">
              Denylist: {Array.isArray(policy.security_controls?.admin_geo_guard?.deny_countries)
                ? policy.security_controls?.admin_geo_guard?.deny_countries.length
                : 0}
            </Badge>
          </div>
          <p>
            This section is read-only. Update admin access location rules in the dedicated page.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to={`/portal/domains/${domain.id}/access-policy`}>Manage in Admin Access Location Rules</Link>
          </Button>
        </CardContent>
      </Card>

      {vulnUiEnabled && (
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle>Vulnerability intelligence</CardTitle>
            <CardDescription>Read-only enrichment that matches your installed components to known issues.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-sm font-medium">Enable vulnerability intelligence enrichment</Label>
                <p className="text-xs text-muted-foreground">Sends plugin/theme slugs and versions for matching only.</p>
              </div>
              <Switch
                checked={vulnEnrichmentEnabled}
                onCheckedChange={handleToggleVulnEnrichment}
                disabled={vulnEnrichmentSaving || !dataSourceEnabled}
                aria-label="Enable vulnerability intelligence"
              />
            </div>
            {!dataSourceEnabled && (
              <p className="text-xs text-warning">
                Vulnerability intelligence is currently disabled globally.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {vulnUiEnabled && (
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle>Data sources</CardTitle>
            <CardDescription>Attribution and privacy disclosure for vulnerability intelligence.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline">Primary: WPVulnerability</Badge>
              <span>Last sync: {formatTimestamp(wpvulnSync)}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Aggregated sources (CVE, Patchstack, WPScan, etc.). Coverage depends on upstream data.
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline">Secondary: Wordfence Intelligence (scanner)</Badge>
              <span>Last sync: {formatTimestamp(wordfenceSync)}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Used for enrichment and cross‑validation only. This does not guarantee complete coverage.
            </div>
            <p>
              This feature only sends component slugs and versions for matching. No WordPress credentials are shared.
            </p>
            <div className="flex flex-wrap gap-3">
              <a className="text-primary underline" href="https://www.wpvulnerability.com/sources/" target="_blank" rel="noreferrer">
                WPVulnerability sources
              </a>
              <a className="text-primary underline" href="https://www.wpvulnerability.net/terms/" target="_blank" rel="noreferrer">
                WPVulnerability terms
              </a>
              <a className="text-primary underline" href="https://www.wordfence.com/intelligence/" target="_blank" rel="noreferrer">
                Wordfence Intelligence terms
              </a>
            </div>
          </CardContent>
      </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving || controlsReadOnly}>
          {isManagedLocked
            ? 'Managed policy locked'
            : rolloutReadOnly
            ? 'Read-only rollout active'
            : saving
            ? 'Saving...'
            : 'Save security controls'}
        </Button>
      </div>
    </div>
  );
}
